import { useState } from 'react'
import {
	FilterContent,
	MultiSelectContent,
	SingleSelectContent,
	RangeContent,
	DateContent
} from '@/components/FilterContent'
import type { FilterOption } from '@/components/FilterContent'

/* ---------- helpers ---------- */

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-16'>
			<h3 className='spaces:text-14 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
				{title}
			</h3>
			{children}
		</div>
	)
}

/* ---------- mock data ---------- */

const BED_OPTIONS: FilterOption[] = [
	{ value: '0', label: 'Studio' },
	{ value: '1', label: 'One Bedroom' },
	{ value: '2', label: 'Two Bedroom' },
	{ value: '3', label: 'Three Bedroom' },
	{ value: '4', label: 'Four Bedroom' }
]

const FLOOR_OPTIONS: FilterOption[] = [
	{ value: 'low', label: 'Low (1–3)' },
	{ value: 'mid', label: 'Mid (4–7)' },
	{ value: 'high', label: 'High (8+)' },
	{ value: 'penthouse', label: 'Penthouse' }
]

const AMENITY_OPTIONS: FilterOption[] = [
	{ value: 'pool', label: 'Pool' },
	{ value: 'gym', label: 'Fitness Center' },
	{ value: 'rooftop', label: 'Rooftop Deck' },
	{ value: 'concierge', label: 'Concierge' },
	{ value: 'parking', label: 'Parking' },
	{ value: 'storage', label: 'Storage' },
	{ value: 'bike', label: 'Bike Storage' },
	{ value: 'lounge', label: 'Resident Lounge' },
	{ value: 'cowork', label: 'Co-working Space' },
	{ value: 'ev', label: 'EV Charging' },
	{ value: 'spa', label: 'Spa & Sauna' },
	{ value: 'pet', label: 'Pet Wash Station' }
]

const VIEW_OPTIONS: FilterOption[] = [
	{ value: 'city', label: 'City View' },
	{ value: 'park', label: 'Park View' },
	{ value: 'water', label: 'Water View' },
	{ value: 'courtyard', label: 'Courtyard View' },
	{ value: 'mountain', label: 'Mountain View' },
	{ value: 'garden', label: 'Garden View' },
	{ value: 'pool', label: 'Pool View' },
	{ value: 'street', label: 'Street View' },
	{ value: 'skyline', label: 'Skyline View' },
	{ value: 'none', label: 'No Preference' }
]

const PRICE_MIN = 500
const PRICE_MAX = 5000

/* ---------- sandbox ---------- */

export function FilterContentSandbox () {
	/* multi-select state */
	const [beds, setBeds] = useState<string[]>([])
	const [amenities, setAmenities] = useState<string[]>([])

	/* single-select state */
	const [floor, setFloor] = useState<string | null>(null)
	const [view, setView] = useState<string | null>(null)

	/* range state */
	const [priceMin, setPriceMin] = useState<number | undefined>(1000)
	const [priceMax, setPriceMax] = useState<number | undefined>(3500)

	/* date state */
	const [moveIn, setMoveIn] = useState('')
	const [calMonth, setCalMonth] = useState(new Date().getMonth())
	const [calYear, setCalYear] = useState(new Date().getFullYear())

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Multi-Select ── */}
			<Section title='Multi-Select'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-24'>
					<div className='spaces:w-[304px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
						<FilterContent
							title='Bedrooms'
							onClear={() => setBeds([])}
							onCancel={() => {}}
							onSave={() => {}}
						>
							<MultiSelectContent
								options={BED_OPTIONS}
								selected={beds}
								onChange={setBeds}
							/>
						</FilterContent>
					</div>
					<div className='spaces:w-[304px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
						<FilterContent
							title='Amenities'
							onClear={() => setAmenities([])}
							onCancel={() => {}}
							onSave={() => {}}
						>
							<MultiSelectContent
								options={AMENITY_OPTIONS}
								selected={amenities}
								onChange={setAmenities}
							/>
						</FilterContent>
					</div>
				</div>
			</Section>

			{/* ── Single-Select ── */}
			<Section title='Single-Select'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-24'>
					<div className='spaces:w-[304px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
						<FilterContent
							title='Floor'
							onClear={() => setFloor(null)}
							onCancel={() => {}}
							onSave={() => {}}
						>
							<SingleSelectContent
								options={FLOOR_OPTIONS}
								selected={floor}
								onChange={setFloor}
							/>
						</FilterContent>
					</div>
					<div className='spaces:w-[304px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
						<FilterContent
							title='View'
							onClear={() => setView(null)}
							onCancel={() => {}}
							onSave={() => {}}
						>
							<SingleSelectContent
								options={VIEW_OPTIONS}
								selected={view}
								onChange={setView}
							/>
						</FilterContent>
					</div>
				</div>
			</Section>

			{/* ── Range ── */}
			<Section title='Range'>
				<div className='spaces:w-[400px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
					<FilterContent
						title='Price'
						onClear={() => {
							setPriceMin(PRICE_MIN)
							setPriceMax(PRICE_MAX)
						}}
						onCancel={() => {}}
						onSave={() => {}}
					>
						<RangeContent
							min={PRICE_MIN}
							max={PRICE_MAX}
							valueMin={priceMin}
							valueMax={priceMax}
							onMinChange={setPriceMin}
							onMaxChange={setPriceMax}
							prefix='$'
							step={50}
						/>
					</FilterContent>
				</div>
			</Section>

			{/* ── Date ── */}
			<Section title='Date'>
				<div className='spaces:w-[312px] spaces:bg-canvas-base spaces:rounded-16 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden'>
					<FilterContent
						title='Move-In Date'
						onClear={() => setMoveIn('')}
						onCancel={() => {}}
						onSave={() => {}}
					>
						<DateContent
							value={moveIn}
							onValueChange={setMoveIn}
							calendarMonth={calMonth}
							calendarYear={calYear}
							onCalendarMonthChange={(m, y) => {
								setCalMonth(m)
								setCalYear(y)
							}}
						/>
					</FilterContent>
				</div>
			</Section>
		</div>
	)
}
